import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import { MapPin, Phone, Star, Search, Store, BadgeCheck } from "lucide-react";

const CATS = ["सभी", "इलेक्ट्रॉनिक्स", "ज्वेलरी", "रेस्तराँ", "कपड़े", "किराना", "क्लिनिक", "स्कूल", "मोबाइल", "अन्य"];

export default function BusinessDirectoryPage() {
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState("सभी");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = cat !== "सभी" ? `?category=${encodeURIComponent(cat)}` : "";
    api.get(`/businesses${params}`).then((r) => setItems(r.data)).finally(() => setLoading(false));
  }, [cat]);

  const filtered = q ? items.filter((b) => (b.name || "").toLowerCase().includes(q.toLowerCase()) || (b.tagline || "").toLowerCase().includes(q.toLowerCase())) : items;

  return (
    <>
      <PublicHeader />
      <section className="bg-gradient-to-r from-[#0F172A] via-[#1E293B] to-[#0F172A] text-white">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="inline-flex items-center gap-2 bg-amber-400/20 text-amber-300 rounded-full px-3 py-1 text-[10px] font-black uppercase tracking-widest mb-2">
            <Store className="w-3 h-3"/>Hyperlocal Business Directory
          </div>
          <h1 className="font-devnagri-heading text-3xl md:text-4xl font-black">सूरजपुर के व्यापारी · एक जगह</h1>
          <p className="text-amber-200/90 mt-1 text-sm">दुकानदार, क्लिनिक, स्कूल, रेस्तराँ — सबकी जानकारी, फोटो, review और directions एक ही जगह</p>
        </div>
      </section>
      <main className="max-w-6xl mx-auto px-4 py-6 pb-24" data-testid="directory-page">
        <div className="flex flex-col md:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-3.5 text-slate-400"/>
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="व्यापार खोजें…"
              className="w-full pl-10 pr-4 py-3 rounded-xl border-2 border-slate-200 focus:border-[#DC2626] outline-none text-sm" data-testid="dir-search"/>
          </div>
          <div className="flex gap-2 overflow-x-auto py-1">
            {CATS.map((c) => (
              <button key={c} onClick={() => setCat(c)}
                className={`whitespace-nowrap px-3 py-2 rounded-full text-xs font-bold transition ${cat === c ? "bg-[#DC2626] text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
                data-testid={`dir-cat-${c}`}>{c}</button>
            ))}
          </div>
        </div>
        {loading ? <div className="text-center py-10 text-slate-500">Loading…</div> :
         filtered.length === 0 ? (
          <div className="text-center py-16 bg-slate-50 rounded-xl border-2 border-dashed border-slate-200">
            <Store className="w-12 h-12 text-slate-400 mx-auto mb-3"/>
            <div className="font-devnagri-heading text-xl font-black text-slate-700">कोई व्यापार नहीं मिला</div>
            <div className="text-sm text-slate-500 mt-1">अपना व्यापार जोड़ने के लिए +91 8463860303 पर संपर्क करें</div>
          </div>
         ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((b) => (
              <Link to={`/biz/${b.slug}`} key={b.slug} className="group rounded-xl overflow-hidden bg-white border-2 border-slate-100 hover:border-[#EA580C] hover:shadow-xl transition" data-testid={`biz-card-${b.slug}`}>
                <div className="relative h-40 bg-gradient-to-br from-slate-200 to-slate-300 overflow-hidden">
                  {b.cover_image ? <img src={b.cover_image} alt={b.name} className="w-full h-full object-cover group-hover:scale-105 transition duration-500"/> :
                   <div className="flex items-center justify-center h-full"><Store className="w-14 h-14 text-slate-400"/></div>}
                  {b.listing_plan === "gold" && <div className="absolute top-2 right-2 bg-gradient-to-r from-amber-500 to-orange-600 text-white text-[10px] font-black px-2 py-1 rounded uppercase tracking-widest">Gold</div>}
                  <div className="absolute bottom-2 left-2 bg-black/60 backdrop-blur text-white text-[10px] font-bold px-2 py-1 rounded">{b.category}</div>
                </div>
                <div className="p-3">
                  <div className="flex items-center gap-1 mb-1">
                    <h3 className="font-devnagri-heading font-black text-[#0F172A] leading-tight truncate flex-1">{b.name}</h3>
                    {b.verified && <BadgeCheck className="w-4 h-4 text-blue-500 shrink-0"/>}
                  </div>
                  {b.tagline && <div className="text-xs text-slate-600 line-clamp-1">{b.tagline}</div>}
                  <div className="flex items-center gap-3 mt-2 text-[11px] text-slate-500">
                    <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3"/>{b.city}</span>
                    {b.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3"/>{b.phone.slice(0, 13)}</span>}
                  </div>
                </div>
              </Link>
            ))}
          </div>
         )}
      </main>
      <PublicFooter />
    </>
  );
}
